/**
 * Tax Report Service
 * 
 * API operations for Finnish Tax Authority forms (Y4, Y5, VAT 101/102).
 */

import { apiClient } from '../client';

export type TaxReportType = 'y4' | 'y5' | 'vat-101' | 'vat-102';

export interface TaxReportRequest {
  organization_id: string;
  fiscal_year?: string;
  year?: number;
  quarter?: number;
  month?: number;
  format?: 'pdf' | 'xml' | 'json';
}

export interface TaxReportHistoryItem {
  id: string;
  organizationId: string;
  type: TaxReportType;
  fiscalYear: number;
  quarter?: number;
  month?: number;
  createdAt: string;
  createdBy: string;
  downloadUrl: string;
  format: 'pdf' | 'xml';
}

export interface Y4FormData {
  organization: {
    name: string;
    businessId: string;
    address: string;
    postalCode: string;
    city: string;
  };
  fiscalPeriod: {
    startDate: string;
    endDate: string;
  };
  incomeStatement: {
    totalIncome: number;
    totalExpenses: number;
    netResult: number;
  };
  membershipFees: number;
  activityIncome: number;
  fundraisingIncome: number;
  grantsReceived: number;
  otherIncome: number;
  personnelCosts: number;
  depreciation: number;
  otherExpenses: number;
  totalAssets: number;
  totalLiabilities: number;
}

export interface Y5FormData {
  organization: {
    name: string;
    businessId: string;
    address: string;
    postalCode: string;
    city: string;
  };
  fiscalPeriod: {
    startDate: string;
    endDate: string;
  };
  incomeStatement: {
    totalIncome: number;
    totalExpenses: number;
    netResult: number;
  };
  maintenanceFees: number;
  parkingFees: number;
  saunaFees: number;
  laundryFees: number;
  otherHousingIncome: number;
  repairCosts: number;
  heatingCosts: number;
  electricityCosts: number;
  waterCosts: number;
  insuranceCosts: number;
  adminCosts: number;
  financingCosts: number;
  totalAssets: number;
  totalLiabilities: number;
}

export interface VatFormData {
  organization: {
    name: string;
    businessId: string;
    address: string;
    postalCode: string;
    city: string;
  };
  reportingPeriod: {
    year: number;
    quarter?: number;
    month?: number;
  };
  vatDetails: {
    domesticSales: number;
    domesticSalesVat: number;
    euServiceSales: number;
    euGoodsSales: number;
    euGoodsPurchases: number;
    euServicePurchases: number;
    imports: number;
    importVat: number;
    deductibleVat: number;
    vatPayable: number;
  };
}

export class TaxReportService {
  /**
   * Generate Y4 tax return (associations/foundations)
   */
  async generateY4(params: TaxReportRequest): Promise<Blob> {
    const queryParams = new URLSearchParams();
    queryParams.set('organization_id', params.organization_id);
    if (params.fiscal_year) queryParams.set('fiscal_year', params.fiscal_year);
    queryParams.set('format', params.format || 'pdf');

    const response = await fetch(
      `${apiClient['baseUrl']}/reports/tax/y4?${queryParams.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': params.format === 'xml' ? 'application/xml' : 'application/pdf',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to generate Y4: ${response.statusText}`);
    }

    return response.blob();
  }

  /**
   * Generate Y5 tax return (housing companies)
   */
  async generateY5(params: TaxReportRequest): Promise<Blob> {
    const queryParams = new URLSearchParams();
    queryParams.set('organization_id', params.organization_id);
    if (params.fiscal_year) queryParams.set('fiscal_year', params.fiscal_year);
    queryParams.set('format', params.format || 'pdf');

    const response = await fetch(
      `${apiClient['baseUrl']}/reports/tax/y5?${queryParams.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': params.format === 'xml' ? 'application/xml' : 'application/pdf',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to generate Y5: ${response.statusText}`);
    }

    return response.blob();
  }

  /**
   * Generate VAT 101 (cash basis)
   */
  async generateVat101(params: TaxReportRequest): Promise<Blob> {
    const queryParams = new URLSearchParams();
    queryParams.set('organization_id', params.organization_id);
    if (params.year) queryParams.set('year', params.year.toString());
    if (params.quarter) queryParams.set('quarter', params.quarter.toString());
    if (params.month) queryParams.set('month', params.month.toString());
    queryParams.set('format', params.format || 'pdf');

    const response = await fetch(
      `${apiClient['baseUrl']}/reports/tax/vat-101?${queryParams.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': params.format === 'xml' ? 'application/xml' : 'application/pdf',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to generate VAT 101: ${response.statusText}`);
    }

    return response.blob();
  }

  /**
   * Generate VAT 102 (accrual basis)
   */
  async generateVat102(params: TaxReportRequest): Promise<Blob> {
    const queryParams = new URLSearchParams();
    queryParams.set('organization_id', params.organization_id);
    if (params.year) queryParams.set('year', params.year.toString());
    if (params.quarter) queryParams.set('quarter', params.quarter.toString());
    if (params.month) queryParams.set('month', params.month.toString());
    queryParams.set('format', params.format || 'pdf');

    const response = await fetch(
      `${apiClient['baseUrl']}/reports/tax/vat-102?${queryParams.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': params.format === 'xml' ? 'application/xml' : 'application/pdf',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to generate VAT 102: ${response.statusText}`);
    }

    return response.blob();
  }

  /**
   * Preview Y4 data (JSON format)
   */
  async previewY4(organizationId: string, fiscalYear: string): Promise<Y4FormData> {
    return apiClient.get<Y4FormData>(
      `/reports/tax/y4?organization_id=${organizationId}&fiscal_year=${fiscalYear}&format=json`
    );
  }

  /**
   * Preview Y5 data (JSON format)
   */
  async previewY5(organizationId: string, fiscalYear: string): Promise<Y5FormData> {
    return apiClient.get<Y5FormData>(
      `/reports/tax/y5?organization_id=${organizationId}&fiscal_year=${fiscalYear}&format=json`
    );
  }

  /**
   * Preview VAT 101 data (JSON format)
   */
  async previewVat101(
    organizationId: string,
    year: number,
    quarter?: number,
    month?: number
  ): Promise<VatFormData> {
    const params = new URLSearchParams();
    params.set('organization_id', organizationId);
    params.set('year', year.toString());
    if (quarter) params.set('quarter', quarter.toString());
    if (month) params.set('month', month.toString());
    params.set('format', 'json');

    return apiClient.get<VatFormData>(`/reports/tax/vat-101?${params.toString()}`);
  }

  /**
   * Preview VAT 102 data (JSON format)
   */
  async previewVat102(
    organizationId: string,
    year: number,
    quarter?: number,
    month?: number
  ): Promise<VatFormData> {
    const params = new URLSearchParams();
    params.set('organization_id', organizationId);
    params.set('year', year.toString());
    if (quarter) params.set('quarter', quarter.toString());
    if (month) params.set('month', month.toString());
    params.set('format', 'json');

    return apiClient.get<VatFormData>(`/reports/tax/vat-102?${params.toString()}`);
  }

  /**
   * Get tax report history (mock implementation - would need backend endpoint)
   */
  async getHistory(_organizationId: string): Promise<TaxReportHistoryItem[]> {
    // This would be a real API call in production
    // For now, return empty array
    return [];
  }

  /**
   * Download a generated report
   */
  downloadReport(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

// Export singleton instance
export const taxReportService = new TaxReportService();
